/**
 * Signal-grade per-file encryption using AES-GCM.
 * Each attachment gets its own random 256-bit key.
 * The key is then bundled inside the E2EE message content.
 */

import { arrayBufferToBase64, base64ToArrayBuffer } from './crypto-utils';

/**
 * Generate a random AES-256-GCM key for encrypting a single file.
 * Returns the key as a base64 string (extractable so it can be shared in the message).
 */
export async function generateFileKey(): Promise<string> {
  const key = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true, // extractable - we need to export & share it
    ['encrypt', 'decrypt']
  );
  const raw = await crypto.subtle.exportKey('raw', key);
  return arrayBufferToBase64(raw);
}

/**
 * Import a base64 file key back into a CryptoKey.
 */
export async function importFileKey(base64Key: string): Promise<CryptoKey> {
  const raw = base64ToArrayBuffer(base64Key);
  return crypto.subtle.importKey(
    'raw',
    raw,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Encrypt file bytes with AES-GCM.
 * Returns IV (12 bytes) + ciphertext as a single ArrayBuffer.
 */
export async function encryptFileBytes(
  data: ArrayBuffer,
  base64Key: string
): Promise<ArrayBuffer> {
  const key = await importFileKey(base64Key);
  const iv = crypto.getRandomValues(new Uint8Array(12));

  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    data
  );

  // Combine: IV (12 bytes) + ciphertext
  const combined = new Uint8Array(iv.length + encrypted.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(encrypted), iv.length);
  return combined.buffer;
}

/**
 * Decrypt file bytes encrypted with encryptFileBytes.
 */
export async function decryptFileBytes(
  encryptedData: ArrayBuffer,
  base64Key: string
): Promise<ArrayBuffer> {
  const key = await importFileKey(base64Key);
  const combined = new Uint8Array(encryptedData);

  const iv = combined.slice(0, 12);
  const ciphertext = combined.slice(12);

  return crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    ciphertext
  );
}
